export const formatCurrency = (value, options = {}) => {
  if (value === null || value === undefined || isNaN(value)) {
    return "$0.00";
  }

  const {
    showCents = true,
    showSymbol = true,
    abbreviate = false,
    decimalPlaces = 0,
  } = options;

  const numValue = parseFloat(value);
  if (isNaN(numValue)) return "$0";

  const absValue = Math.abs(numValue);
  const isNegative = numValue < 0;

  let formatted;

  if (abbreviate) {
    if (absValue >= 1000000000) {
      formatted = `${(absValue / 1000000000).toFixed(1)}B`;
    } else if (absValue >= 1000000) {
      formatted = `${(absValue / 1000000).toFixed(1)}M`;
    } else if (absValue >= 1000) {
      formatted = `${(absValue / 1000).toFixed(1)}K`;
    } else {
      formatted = absValue.toFixed(showCents ? decimalPlaces : 0);
    }
  } else {
    try {
      formatted = new Intl.NumberFormat("en-US", {
        style: showSymbol ? "currency" : "decimal",
        currency: "USD",
        minimumFractionDigits: showCents ? decimalPlaces : 0,
        maximumFractionDigits: showCents ? decimalPlaces : 0,
      }).format(absValue);

      if (!showSymbol && formatted.startsWith("$")) {
        formatted = formatted.substring(1);
      }
    } catch (error) {
      formatted = `${showSymbol ? "$" : ""}${absValue.toFixed(showCents ? decimalPlaces : 0)}`;
    }
  }

  return isNegative ? `-${formatted.replace("-", "")}` : formatted;
};

/**
 * Exposure / VaR dollar amounts: shown in millions once they reach $1M
 * ($12.34M), full dollars below that ($123,456), and negatives accounting-style
 * in brackets (($12.34M)). Pair negatives with negativeColor() for the red.
 */
export const formatExposure = (value) => {
  if (value === null || value === undefined || isNaN(value)) return "—";
  const v = Number(value);
  const abs = Math.abs(v);
  const body =
    abs >= 1e6
      ? `$${(abs / 1e6).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}M`
      : `$${Math.round(abs).toLocaleString("en-US")}`;
  return v < 0 && Math.round(abs) > 0 ? `(${body})` : body;
};

/** Red for negative values (MUI theme colour), otherwise inherit. */
export const negativeColor = (value) => (Number(value) < 0 ? "error.main" : "inherit");

/**
 * Short market value (SMV): the backend reports it as a positive magnitude, but
 * it is a short (negative) exposure, so it is shown negative - e.g. 1500000 ->
 * "($1.50M)". Zero stays "$0". Pair with SMV_NEGATIVE_COLOR for the red.
 */
export const formatShortExposure = (value) =>
  value === null || value === undefined || isNaN(value) ? "—" : formatExposure(-Math.abs(Number(value)));

export const SMV_NEGATIVE_COLOR = "error.main";

/**
 * Dollar-magnitude formatter for decision-relevant figures (NAV, LMV, exposure)
 * shown on the Marketing Report preview and its pre-generation dialogs — e.g.
 * 114750000 -> "$114.75M". Distinct from formatCurrency's `abbreviate` option
 * (1 decimal place, no $ sign): this carries the dollar sign, capital letter
 * suffix, 2 decimal places, and explicit negative-sign handling, matching what
 * the report preview already shows a user moments before generation.
 */
export const formatUSDMagnitude = (value) => {
  if (value === null || value === undefined || isNaN(value)) return '$0';
  const v = Number(value);
  const abs = Math.abs(v);
  const sign = v < 0 ? '-' : '';
  if (abs >= 1e9) return `${sign}$${(abs / 1e9).toFixed(2)}B`;
  if (abs >= 1e6) return `${sign}$${(abs / 1e6).toFixed(2)}M`;
  if (abs >= 1e3) return `${sign}$${(abs / 1e3).toFixed(1)}K`;
  return `${sign}$${abs.toFixed(0)}`;
};

export const formatNumber = (value, decimalPlaces = 2) => {
  if (value === null || value === undefined || isNaN(value)) {
    return "0.00";
  }

  const numValue = parseFloat(value);
  if (isNaN(numValue)) return "0.00";

  try {
    return new Intl.NumberFormat("en-US", {
      minimumFractionDigits: decimalPlaces,
      maximumFractionDigits: decimalPlaces,
    }).format(numValue);
  } catch (error) {
    return numValue.toFixed(decimalPlaces);
  }
};

export const formatBps = (value, decimalPlaces = 1) => {
  if (value === null || value === undefined || isNaN(value)) {
    return "0.0 bps";
  }

  const numValue = parseFloat(value);
  if (isNaN(numValue)) return "0.0 bps";

  return `${numValue.toFixed(decimalPlaces)} bps`;
};

export const formatPercent = (value, decimalPlaces = 1) => {
  if (value === null || value === undefined || isNaN(value)) {
    return "0.0%";
  }

  const numValue = parseFloat(value);
  if (isNaN(numValue)) return "0.0%";

  try {
    return new Intl.NumberFormat("en-US", {
      style: "percent",
      minimumFractionDigits: decimalPlaces,
      maximumFractionDigits: decimalPlaces,
    }).format(numValue / 100);
  } catch (error) {
    return `${numValue.toFixed(decimalPlaces)}%`;
  }
};

export const formatCompactNumber = (value) => {
  if (value === null || value === undefined || isNaN(value)) {
    return "0";
  }

  const numValue = parseFloat(value);
  if (isNaN(numValue)) return "0";

  try {
    return new Intl.NumberFormat("en-US", {
      notation: "compact",
      maximumFractionDigits: 1,
    }).format(numValue);
  } catch (error) {
    // Fallback for older browsers
    const absValue = Math.abs(numValue);
    const isNegative = numValue < 0;

    let formatted;
    if (absValue >= 1000000000) {
      formatted = `${(absValue / 1000000000).toFixed(1)}B`;
    } else if (absValue >= 1000000) {
      formatted = `${(absValue / 1000000).toFixed(1)}M`;
    } else if (absValue >= 1000) {
      formatted = `${(absValue / 1000).toFixed(1)}K`;
    } else {
      formatted = absValue.toString();
    }

    return isNegative ? `-${formatted}` : formatted;
  }
};

export const formatDate = (date, format = "short") => {
  if (!date) return "";

  const dateObj = typeof date === "string" ? new Date(date) : date;
  if (isNaN(dateObj.getTime())) return "";

  try {
    switch (format) {
      case "short":
        return new Intl.DateTimeFormat("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        }).format(dateObj);

      case "long":
        return new Intl.DateTimeFormat("en-US", {
          weekday: "long",
          year: "numeric",
          month: "long",
          day: "numeric",
        }).format(dateObj);

      case "time":
        return new Intl.DateTimeFormat("en-US", {
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
        }).format(dateObj);

      case "datetime":
        return new Intl.DateTimeFormat("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
        }).format(dateObj);

      case "iso":
        return dateObj.toISOString().split("T")[0];

      default:
        return dateObj.toLocaleDateString("en-US", { timeZone: "UTC" });
    }
  } catch (error) {
    return dateObj.toLocaleDateString("en-US", { timeZone: "UTC" });
  }
};

export const calculateChange = (current, previous) => {
  if (!previous || previous === 0) return 0;
  if (current === null || current === undefined || isNaN(current)) return 0;
  if (previous === null || previous === undefined || isNaN(previous)) return 0;

  const currentVal = parseFloat(current);
  const previousVal = parseFloat(previous);

  if (isNaN(currentVal) || isNaN(previousVal) || previousVal === 0) return 0;

  return ((currentVal - previousVal) / Math.abs(previousVal)) * 100;
};

export const formatChange = (current, previous, isPercentage = true) => {
  const change = calculateChange(current, previous);

  if (change === 0) {
    return isPercentage ? "0.0%" : "0.00";
  }

  const prefix = change > 0 ? "+" : "";
  const suffix = isPercentage ? "%" : "";

  return `${prefix}${change.toFixed(1)}${suffix}`;
};

export const getValueColor = (value, type = "pnl") => {
  if (value === null || value === undefined || isNaN(value)) {
    return "text.secondary";
  }

  const numValue = parseFloat(value);
  if (isNaN(numValue)) return "text.secondary";

  switch (type) {
    case "pnl":
    case "currency":
      return numValue >= 0 ? "success.main" : "error.main";

    case "change":
      return numValue > 0
        ? "success.main"
        : numValue < 0
          ? "error.main"
          : "text.secondary";

    default:
      return "text.primary";
  }
};

export const formatTableCell = (value, columnType, options = {}) => {
  switch (columnType) {
    case "currency":
      return formatCurrency(value, options);

    case "number":
      return formatNumber(value, options.decimalPlaces);

    case "percent":
      return formatPercent(value, options.decimalPlaces);

    case "date":
      return formatDate(value, options.format);

    case "compact":
      return formatCompactNumber(value);

    default:
      return value?.toString() || "";
  }
};

export const sanitizeNumericValue = (value) => {
  if (value === null || value === undefined || value === "") {
    return 0;
  }

  const numValue = parseFloat(value);
  return isNaN(numValue) ? 0 : numValue;
};

export const createFormatter = (type, options = {}) => {
  return (value) => formatTableCell(value, type, options);
};

export const currencyFormatter = createFormatter("currency");
export const percentFormatter = createFormatter("percent");
export const numberFormatter = createFormatter("number");
export const compactFormatter = createFormatter("compact");
export const dateFormatter = createFormatter("date");
